
import { SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces';
import { Profile, updateLifeExpectancy } from 'app/redux/Profile/model'
import { Gender } from 'app/redux/Profile/model/interfaces'
import * as _ from 'lodash'
import { memoize } from 'lodash';
import * as moment from 'moment'

// define the names of all the columns (properties) in a profile's 'extraInfo'
const ColumnNames = {
    ssnID: 'SSN / ID',
    memberLastName: 'Last Name',
    memberFirstName: 'First Name',
    memberDOB: 'Birth Date',
    hireDate: 'Hire Date',
    memberGender: 'Sex Code',
    statusCode: 'Status Code',
    spouseFirstName: 'Spouse First Name',
    spouseLastName: 'Spouse Last Name',
    spouseDOB: 'Spouse Birth Date',
    spouseGender: 'Spouse Sex Code',
    insideHours: 'Inside Hours',
    residentialHours: 'Residential Hours',
    teledataHours: 'Teledata Hours',
    totalPreviousYrsHours: 'Total Previous Yr Hours',
    insideCredits: 'Inside Credits',
    insideCreditsValue: 'Inside Credit Value',
    residentialCredits: 'Residential Credits',
    residentialCreditsValue: 'Residential Credits Value',
    teledataCredits: 'Teledata Credits',
    teledateCreditsValue: 'Teledata Credits Value',
    totalFutureCredits: 'Total Future Credits',
    futureService: 'Future Service',
    futureServiceValue: 'Future Service Value',
    activeWelfareDollarBank: 'Active Welfare Dollar Bank',
    retireeWelfareDollarBank: 'Retiree Welfare Dollar Bank',
    profitSharingBalance: 'Profit Sharing Balance',
    futureServiceBucket: 'Future Service Bucket'
}

const genderMap = {
    M: Gender.male,
    F: Gender.female
}

const gm = (g: string) => genderMap[g] || Gender.unidentified

/**
 * Populates a profile with the information found in 'extraInfo'.
 *
 * @param profile - the profile to load.
 */
function loadProfilePresets(profile: Profile, needNEBF = true, needIO = true): Promise<Profile> {
    const ex = profile.extraInfo

    const member = profile.getHeadOfHousehold()
    member.firstName = ex[ColumnNames.memberFirstName]
    member.lastName = ex[ColumnNames.memberLastName]
    member.dob = moment.utc(ex[ColumnNames.memberDOB])
    member.gender = gm(ex[ColumnNames.memberGender])

    const sln = ex[ColumnNames.spouseLastName]
    if (sln) {
        const spouse = profile.getSpouse()
        spouse.firstName = ex[ColumnNames.spouseFirstName]
        spouse.lastName = sln
        spouse.dob = moment.utc(ex[ColumnNames.spouseDOB])
        spouse.gender = gm(ex[ColumnNames.spouseGender])
    }

    const beginPaymentsAge = moment.duration(65, 'years')
    const formOfPayment = SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor
    const futureWeeksPerYear = 52

    profile.pension.local145 = {
        beginPaymentsAge,
        formOfPayment,
        hireDate: moment(),
        statusCode: '',
        insideHours: 0,
        residentialHours: 0,
        teledataHours: 0,
        totalPreviousYrsHours: 0,
        insideCredits: 0,
        insideCreditsValue: 0,
        residentialCredits: 0,
        residentialCreditsValue: 0,
        teledataCredits: 0,
        teledateCreditsValue: 0,
        totalFutureCredits: 0,
        futureService: null,
        futureServiceValue: null,
        activeWelfareDollarBank: null,
        retireeWelfareDollarBank: null,
        profitSharingBalance: 0,
        futureServiceBucket: null
    }

    if (needIO) {
        profile.pension.IO = {
            beginPaymentsAge,
            formOfPayment: SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor,
            participatesInIO: false,
            yearsOfAMembership: moment.duration(0)
        }
    }

    if (needNEBF) {
        profile.pension.NEBF = {
            beginPaymentsAge,
            formOfPayment: SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor,
            currentYearsService: moment.duration(0)
        }
    }

    loadUpgradeableData(profile)

    return updateLifeExpectancy(member)
    .then(() => {
        if (sln) {
            return updateLifeExpectancy(profile.getSpouse())
            .then(() => profile)
        }
        return profile
    })
}


function loadFamilyData(profile: Profile) {
    const ex = profile.extraInfo

    const member = profile.getHeadOfHousehold()
    member.firstName = ex[ColumnNames.memberFirstName]
    member.lastName = ex[ColumnNames.memberLastName]
    member.dob = moment.utc(ex[ColumnNames.memberDOB])
    member.gender = gm(ex[ColumnNames.memberGender])

    const sln = ex[ColumnNames.spouseLastName]
    if (sln) {
        const spouse = profile.getSpouse()
        spouse.firstName = ex[ColumnNames.spouseFirstName]
        spouse.lastName = sln
        spouse.dob = moment.utc(ex[ColumnNames.spouseDOB])
        spouse.gender = gm(ex[ColumnNames.spouseGender])
    }
}

function loadUpgradeableData(profile: Profile) {

    // can't have other customer data here (sometimes, during a port, it creeps in)
    delete profile.pension.local697
    delete profile.pension.local701
    delete profile.pension.local705

    const ex = profile.extraInfo
    loadFamilyData(profile)

    profile.pension.local145 = {
        ...profile.pension.local145,
        statusCode: ex[ColumnNames.statusCode],
        insideHours: ex[ColumnNames.insideHours],
        residentialHours: ex[ColumnNames.residentialHours],
        teledataHours: ex[ColumnNames.teledataHours],
        totalPreviousYrsHours: ex[ColumnNames.totalPreviousYrsHours],
        insideCredits: ex[ColumnNames.insideCredits],
        insideCreditsValue: ex[ColumnNames.insideCreditsValue],
        residentialCredits: ex[ColumnNames.residentialCredits],
        residentialCreditsValue: ex[ColumnNames.residentialCreditsValue],
        teledataCredits: ex[ColumnNames.teledataCredits],
        teledateCreditsValue: ex[ColumnNames.teledateCreditsValue],
        totalFutureCredits: ex[ColumnNames.totalFutureCredits],
        futureService: ex[ColumnNames.futureService],
        futureServiceValue: ex[ColumnNames.futureServiceValue],
        activeWelfareDollarBank: ex[ColumnNames.activeWelfareDollarBank],
        retireeWelfareDollarBank: ex[ColumnNames.retireeWelfareDollarBank],
        profitSharingBalance: ex[ColumnNames.profitSharingBalance],
        futureServiceBucket: ex[ColumnNames.futureServiceBucket]
    }

    profile.pension.NEBF.currentYearsService = profile.pension.NEBF.currentYearsService || moment.duration(0)
    profile.pension.IO.yearsOfAMembership = profile.pension.IO.yearsOfAMembership || moment.duration(0)

    // pick up pension balance

    const memberSavings = profile.getRetirementForHeadOfHouseHold()
    memberSavings.fixedBalances.retirementSavings = ex[ColumnNames.profitSharingBalance]
}

/**
 * Returns an array of the information in 'extraInfo' decribed by a column name.
 *
 * @param profile - the profile containing the 'extraInfo'
 * @param columnNames - a list of columns required
 */
function pickColumns(profile: Profile, columnNames: string[]): any[] {
    if (!profile.extraInfo) { return [] }
    return _.map(columnNames, (propName) => profile.extraInfo[propName])
}

export {
    ColumnNames,
    loadProfilePresets,
    pickColumns,
    loadUpgradeableData
}



// WEBPACK FOOTER //
// ./src/config/145/ETL/index.ts